// «Categorías y conceptos» de Finanzas (7-oct-2026, Mabel: «que tenga la opción de dar de alta más conceptos,
// en el apartado poder dar de alta más categorías, cada categoría poder poner conceptos como Agua, Teléfono»).
// Apartado › Categoría › Concepto. Nada se borra: se archiva (deja de salir al capturar, el historial queda).
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Pencil, Archive, ArchiveRestore, Check, FolderTree } from 'lucide-react';
import { useToast } from '../../../context/ToastContext';
import { getCatalog, createFinCategory, patchFinCategory, createFinConcept, patchFinConcept, FinCatalog, FinSection } from '../api';
import { invalidarCatalogo } from './ConceptoSelect';

const inputCls =
  'w-full rounded-lg border border-line bg-surface px-3 py-1.5 text-sm text-content outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30';

/** Texto editable en su sitio: lápiz → input → Enter guarda, Esc cancela. */
function Editable({ value, onSave, className }: { value: string; onSave: (v: string) => Promise<void>; className?: string }) {
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  if (!edit) {
    return (
      <span className={`group inline-flex min-w-0 items-center gap-1 ${className || ''}`}>
        <span className="truncate">{value}</span>
        <button onClick={() => setEdit(true)} className="shrink-0 text-content-3 opacity-60 transition hover:text-content group-hover:opacity-100" title="Renombrar"><Pencil size={12} /></button>
      </span>
    );
  }
  const guardar = async () => { if (v.trim() && v.trim() !== value) await onSave(v.trim()); setEdit(false); };
  return (
    <span className="inline-flex min-w-0 flex-1 items-center gap-1">
      <input autoFocus value={v} onChange={(e) => setV(e.target.value)} className={inputCls}
        onKeyDown={(e) => { if (e.key === 'Enter') guardar(); if (e.key === 'Escape') { setV(value); setEdit(false); } }} />
      <button onClick={guardar} className="shrink-0 rounded-md bg-accent-solid p-1.5 text-on-accent"><Check size={13} /></button>
    </span>
  );
}

/** Input + botón para dar de alta (categoría o concepto). */
function Alta({ placeholder, onAdd }: { placeholder: string; onAdd: (nombre: string) => Promise<void> }) {
  const [v, setV] = useState('');
  const [busy, setBusy] = useState(false);
  const go = async () => { if (!v.trim()) return; setBusy(true); try { await onAdd(v.trim()); setV(''); } finally { setBusy(false); } };
  return (
    <div className="flex items-center gap-1.5">
      <input value={v} onChange={(e) => setV(e.target.value)} placeholder={placeholder} className={inputCls}
        onKeyDown={(e) => { if (e.key === 'Enter') go(); }} />
      <button onClick={go} disabled={!v.trim() || busy}
        className="flex shrink-0 items-center gap-1 rounded-lg border border-line px-2.5 py-1.5 text-xs font-semibold text-content-2 transition hover:bg-surface-2 disabled:opacity-40">
        <Plus size={13} /> Agregar
      </button>
    </div>
  );
}

export default function CatalogoModal({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const [cat, setCat] = useState<FinCatalog[] | null>(null);
  const [sec, setSec] = useState<FinSection>('egreso');
  const [verArchivados, setVerArchivados] = useState(false);

  const load = useCallback(() => getCatalog(true).then((r) => setCat(r.data)).catch((e) => toast.error(e?.message || 'No se pudo cargar')), [toast]);
  useEffect(() => { load(); }, [load]);

  async function hacer(fn: () => Promise<unknown>, ok?: string) {
    try { await fn(); invalidarCatalogo(); if (ok) toast.success(ok); await load(); }
    catch (e: any) { toast.error(e?.message || 'No se pudo guardar'); }
  }

  const actual = cat?.find((s) => s.section === sec);
  const categorias = (actual?.categorias || []).filter((c) => verArchivados || c.active);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true">
      <div className="modal-overlay absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="modal-panel relative flex max-h-[92vh] w-full max-w-2xl flex-col rounded-t-2xl border border-line bg-surface shadow-xl sm:rounded-2xl">
        <div className="flex items-center justify-between border-b border-line px-5 py-4">
          <h3 className="flex items-center gap-2 font-display text-lg font-bold text-content"><FolderTree size={18} className="text-accent-ink" /> Categorías y conceptos</h3>
          <button onClick={onClose} className="text-content-3 transition hover:text-content" aria-label="Cerrar"><X size={18} /></button>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-5 py-2.5">
          <div className="inline-flex flex-wrap rounded-lg border border-line bg-surface p-0.5">
            {(cat || []).map((s) => (
              <button key={s.section} onClick={() => setSec(s.section)}
                className={`rounded-md px-3 py-1 text-xs font-medium transition ${sec === s.section ? 'bg-accent text-on-accent shadow-sm' : 'text-content-2 hover:text-content'}`}>
                {s.label}
              </button>
            ))}
          </div>
          <label className="inline-flex items-center gap-1.5 text-xs text-content-3">
            <input type="checkbox" checked={verArchivados} onChange={(e) => setVerArchivados(e.target.checked)} /> Ver archivados
          </label>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {sec === 'ingreso' && (
            <p className="mb-3 rounded-lg bg-surface-2 px-3 py-2 text-xs text-content-2">
              Los ingresos se acomodan solos según el cobro (mensualidad, inscripción, vitrina…). Un concepto nuevo de ingreso
              se elige al cobrar con tipo «Otro» (por ejemplo, clases privadas o renta del salón).
            </p>
          )}
          {!cat ? <p className="py-6 text-center text-sm text-content-3">Cargando…</p> : (
            <div className="space-y-4">
              {categorias.map((c) => (
                <div key={c.category_id} className={`rounded-xl border border-line ${c.active ? '' : 'opacity-60'}`}>
                  <div className="flex items-center justify-between gap-2 border-b border-line bg-surface-2/50 px-3 py-2">
                    <Editable value={c.name} className="text-sm font-semibold text-content"
                      onSave={(v) => hacer(() => patchFinCategory(c.category_id, { name: v }), 'Categoría renombrada')} />
                    <button onClick={() => hacer(() => patchFinCategory(c.category_id, { active: !c.active }), c.active ? 'Categoría archivada' : 'Categoría restaurada')}
                      className="flex shrink-0 items-center gap-1 text-[11px] text-content-3 hover:text-content" title={c.active ? 'Archivar (deja de salir al capturar)' : 'Restaurar'}>
                      {c.active ? <><Archive size={12} /> Archivar</> : <><ArchiveRestore size={12} /> Restaurar</>}
                    </button>
                  </div>
                  <ul className="divide-y divide-line/60">
                    {c.conceptos.filter((k) => verArchivados || k.active).map((k) => (
                      <li key={k.concept_id} className={`flex items-center justify-between gap-2 px-3 py-1.5 ${k.active ? '' : 'opacity-60'}`}>
                        <Editable value={k.name} className="text-sm text-content-2"
                          onSave={(v) => hacer(() => patchFinConcept(k.concept_id, { name: v }), 'Concepto renombrado')} />
                        <button onClick={() => hacer(() => patchFinConcept(k.concept_id, { active: !k.active }), k.active ? 'Concepto archivado' : 'Concepto restaurado')}
                          className="shrink-0 text-content-3 hover:text-content" title={k.active ? 'Archivar' : 'Restaurar'}>
                          {k.active ? <Archive size={13} /> : <ArchiveRestore size={13} />}
                        </button>
                      </li>
                    ))}
                  </ul>
                  {c.active && (
                    <div className="border-t border-line px-3 py-2">
                      <Alta placeholder={`Nuevo concepto en ${c.name} (ej. Agua)`}
                        onAdd={(n) => hacer(() => createFinConcept(c.category_id, n), 'Concepto agregado')} />
                    </div>
                  )}
                </div>
              ))}
              <div className="rounded-xl border border-dashed border-line px-3 py-3">
                <p className="mb-1.5 text-xs font-semibold text-content-2">Nueva categoría en {actual?.label}</p>
                <Alta placeholder="Ej. Impuestos y trámites" onAdd={(n) => hacer(() => createFinCategory(sec, n), 'Categoría agregada')} />
              </div>
            </div>
          )}
        </div>
        <p className="border-t border-line px-5 py-2.5 text-[11px] text-content-3">
          Nada se borra: archivar solo lo quita de la lista al capturar; los gastos que ya lo usan lo conservan.
        </p>
      </div>
    </div>,
    document.body
  );
}
